import { useAuthStatus, rememberRecoveryRoute, restoredRoute } from "../lib/authRecovery";
import { useEffect, useState, useRef } from "react";
import { accountScope } from "../lib/accountScope";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { accountMessage, isReplacementReason, AuthFailure } from "../lib/auth";
import { ErrorState, LoadingState } from "./Feedback";
import { MyAccountPanel } from "../pages/MyAccountPanel";

export function RequireAccount() {
  const cache = useQueryClient();
  const session = useAuthStatus();
  const location = useLocation();
  const navigate = useNavigate();
  const previousIdentity = useRef<string | undefined>(undefined);
  const user = session.data?.user;
  const scope = session.data ? accountScope(session.data) : undefined;
  const [readyScope, setReadyScope] = useState<string>();
  const allowed = !session.isError && session.data?.connected && user && !session.data?.reason && user.entitlement === "active" && !user.mustChangePassword
    && (!user.products || user.products.sales || user.products.video);
  useEffect(() => {
    if (!allowed || !session.data || !user) return;
    const identity = JSON.stringify([session.data.appId, user.id]);
    if (previousIdentity.current && previousIdentity.current !== identity) {
      navigate(restoredRoute(session.data), {replace:true});
    } else {
      rememberRecoveryRoute(session.data, location.pathname + location.search + location.hash);
    }
    previousIdentity.current = identity;
  }, [allowed, session.data, user, location, navigate]);
  useEffect(() => {
    if (!scope || scope === readyScope) return;
    // Unmount old account views first, then cancel old reads before remounting.
    // Cancellation also prevents late responses repopulating the shared cache.
    const oldAccount = { predicate: (query: {queryKey: readonly unknown[]}) => query.queryKey[0] !== "auth-status" };
    void cache.cancelQueries(oldAccount);
    cache.removeQueries(oldAccount);
    cache.setQueryData(["auth-session"], user || null);
    setReadyScope(scope);
  }, [scope, readyScope, user, cache]);
  useEffect(() => {
    if (allowed || session.isLoading) return;
    void cache.cancelQueries({ predicate: query => !String(query.queryKey[0]).startsWith("auth-") });
    cache.removeQueries({ predicate: query => !String(query.queryKey[0]).startsWith("auth-") });
  }, [allowed, session.isLoading, cache]);
  if (session.isLoading) return <main className="account-gate"><LoadingState label="正在验证软件账号" /></main>;
  if (session.recovering) return <main className="account-gate"><h1>正在恢复连接</h1><p role="status">{session.recoveryMessage}</p><button onClick={() => void session.refetch({cancelRefetch:false})}>重新检查连接</button><a href="/local-control">停止本机手机任务</a></main>;
  if (session.isError) return <main className="account-gate"><ErrorState error={session.error instanceof AuthFailure ? session.error : new AuthFailure("AUTH_UNAVAILABLE")} onRetry={() => session.refetch()} /><a href="/local-control">停止本机手机任务</a></main>;
  if (scope !== readyScope) return <main className="account-gate"><LoadingState label="正在切换软件账号" /></main>;
  if (!user) return <Navigate replace to={`/login${session.data?.reason && session.data.reason !== "SIGNED_OUT" ? `?reason=${encodeURIComponent(session.data.reason)}` : ""}`} />;
  if (user.mustChangePassword) return <Navigate replace to="/password-reset" />;
  if (!allowed) return <main className="account-gate"><h1>{isReplacementReason(session.data?.reason) ? "账号已在另一台电脑登录" : "软件暂不可用"}</h1><p role="alert">{accountMessage(session.data?.reason || "APP_NOT_GRANTED")}</p><a href="/local-control">停止本机手机任务</a><MyAccountPanel /></main>;
  return <Outlet key={scope} />;
}
